import { BooLogo } from "@/components/BooLogo";
import { DiscordIcon } from "@/components/DiscordIcon";
import { GchatIcon } from "@/components/GchatIcon";
import { InlineError } from "@/components/QueryState";
import { SlackIcon } from "@/components/SlackIcon";
import { TeamsIcon } from "@/components/TeamsIcon";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { useFeatureFlag } from "@/lib/feature-flag";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useCatalogActivity } from "./catalog-activity";
import { usePreview } from "./preview-context";
import { CosSlackConnect } from "./cos/CosSlackConnect";
import { useSession } from "@/lib/auth-client";
import { firstName } from "./cos/types";
import { DefaultChannelStep } from "./DefaultChannelStep";
import { channelLabel, type DefaultChannelChoice } from "@/lib/default-channel";

// useCompleteOnboarding stays — slack's "joined existing org" branch
// and the skip button both fire it. The picker tiles handle their own
// onboarding.complete + navigate.
function useCompleteOnboarding() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => api.onboarding.complete(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
    },
  });
  return {
    isPending: mutation.isPending,
    // Land the user on their new Boo's Integrations page (where they can wire
    // up more channels), not the bare dashboard. Falls back to "/" if the
    // org/container id isn't available for some reason.
    completeAndGo: (orgId?: string, containerId?: string) =>
      mutation.mutate(undefined, {
        onSuccess: () =>
          orgId && containerId
            ? navigate({
                to: "/orgs/$orgId/containers/$containerId/integrations",
                params: { orgId, containerId },
                replace: true,
              })
            : navigate({ to: "/", replace: true }),
      }),
  };
}

export function ConnectStep() {
  useDocumentTitle("Connect");
  const queryClient = useQueryClient();
  const { posture } = usePreview();
  // Chief of staff: same platform tiles, copy about one person and a DM.
  const isCos = posture === "chief-of-staff";
  const { data: session } = useSession();
  // Slack bot names cap at 30 characters; a long first name falls back.
  const execFirst = firstName(session?.user.name ?? "");
  const cosSlackName =
    execFirst && `${execFirst}'s CoS`.length <= 30 ? `${execFirst}'s CoS` : "CoS";
  const completeOnboarding = useCompleteOnboarding();
  // Both platform tiles leave the SPA with a full-page navigation, which
  // would kill the page-level listeners of a hub connect flow started on
  // the provisioning screen. Hold the tiles until it settles.
  const { connecting } = useCatalogActivity();
  const connectBusy = connecting > 0;

  // Teams is enabled for all users (PostHog flag gate removed).
  const teamsFlagEnabled = true;
  // Discord is rolling out behind a PostHog flag while the backend
  // endpoints land — same pattern Teams used pre-GA.
  const discordFlagEnabled = useFeatureFlag("discord-integration");

  // URL params from OAuth callbacks. ?slack=joined is the "joined existing
  // org" branch; ?slack=error / ?gchat=error are connection failures.
  const [slackResult] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("slack");
  });
  const [gchatResult] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("gchat");
  });
  const [teamsResult] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("teams");
  });
  const [discordResult] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("discord");
  });
  // Present when the workspace the user tried to connect is already linked
  // to a different Boo workspace (backend returns ?slack=workspace_taken).
  const [takenWorkspace] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return new URL(window.location.href).searchParams.get("workspace");
  });

  // Strip OAuth-callback noise from the URL on mount and invalidate
  // status queries that the callback would have updated server-side.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (
      url.searchParams.has("slack") ||
      url.searchParams.has("gchat") ||
      url.searchParams.has("teams") ||
      url.searchParams.has("discord") ||
      url.searchParams.has("workspace")
    ) {
      queryClient.invalidateQueries({ queryKey: ["slack-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["gchat-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["teams-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["discord-boo-status"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
      url.searchParams.delete("slack");
      url.searchParams.delete("gchat");
      url.searchParams.delete("teams");
      url.searchParams.delete("discord");
      url.searchParams.delete("workspace");
      window.history.replaceState({}, "", url.toString());
    }
  }, [queryClient]);

  // Single source of truth for the container + active org. The backend
  // returns these on /state once the container is fully provisioned —
  // by the time the user lands on /onboarding/connect, /state.step has
  // flipped to "connect", which means containerId, activeOrganizationId,
  // and containerName are all populated.
  const onboardingState = useQuery({
    queryKey: ["onboarding-state"],
    queryFn: () => api.onboarding.state(),
  });

  const containerId = onboardingState.data?.containerId;
  const orgId = onboardingState.data?.activeOrganizationId;
  const botName = onboardingState.data?.containerName?.trim() || "Boo";

  // Capability gate: only offer Discord when this Boo's build ships the
  // adapter (pool Boos can predate it, and users can't update a brain
  // themselves). Presence in the platform report is the capability signal.
  const platformsQuery = useQuery({
    queryKey: ["channel-platforms", containerId],
    queryFn: () => api.containers.channels.platforms(containerId ?? ""),
    enabled: !!containerId,
  });
  const brainSupportsDiscord =
    platformsQuery.data?.platforms.some((p) => p.platform === "discord") ===
    true;

  const slackConnected =
    slackResult === "connected" || !!onboardingState.data?.slackConnected;
  const gchatConnected =
    gchatResult === "connected" || !!onboardingState.data?.gchatConnected;
  const teamsConnected =
    teamsResult === "connected" || !!onboardingState.data?.teamsConnected;
  const discordConnected =
    discordResult === "connected" || !!onboardingState.data?.discordConnected;

  // teamId for the success screen's "Open Slack" link —
  // app.slack.com/client/<teamId> opens the workspace without needing
  // the vanity domain (which the OAuth scopes don't expose). Same query
  // key the OAuth-callback cleanup effect above invalidates.
  const slackStatus = useQuery({
    queryKey: ["slack-boo-status"],
    queryFn: () => api.slack.boo.status(),
    enabled: slackConnected,
  });

  const navigate = useNavigate();

  // Set once the default-channel step finishes in this visit; the success
  // screen names the channel. Server state (slackKickoffPending) decides
  // whether the step shows at all, so a reload mid-step lands back on it.
  const [defaultChoice, setDefaultChoice] = useState<DefaultChannelChoice | null>(null);

  // Slack OAuth — pass the now-existing containerId so the callback
  // links the install row to this brain directly (slack-boo.ts state JWT
  // carries containerId; the post-OAuth handler pushes credentials to
  // the running container in one shot, no frontend follow-up needed).
  //
  // If the backend reports `already_installed` (409), the workspace is
  // already linked to this org — refresh state so the success branch
  // below renders instead of bubbling a raw "already_installed" string.
  const addToSlack = useMutation({
    mutationFn: async () => {
      const { url } = await api.slack.boo.install(
        containerId,
        "/onboarding/connect",
      );
      return url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "already_installed") {
        queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
      }
    },
  });

  // Teams mirrors Slack: OAuth install returns a URL we full-page navigate
  // to; the callback links the install row to this container and redirects
  // back with ?teams=connected. 409 already_installed → refresh state.
  const addToTeams = useMutation({
    mutationFn: async () => {
      const { url } = await api.teams.boo.install(
        containerId,
        "/onboarding/connect",
      );
      return url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "already_installed") {
        queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
      }
    },
  });

  // Discord mirrors Teams: OAuth install returns a URL we full-page navigate
  // to; the callback links the install row to this container and redirects
  // back with ?discord=connected. 409 already_installed → refresh state.
  const addToDiscord = useMutation({
    mutationFn: async () => {
      const { url } = await api.discord.boo.install(
        containerId,
        "/onboarding/connect",
      );
      return url;
    },
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "already_installed") {
        queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
      }
    },
  });

  // Google Chat is custom-setup only — no marketplace OAuth. Go straight to
  // the Boo's Google Chat connection page, which renders the BYO
  // custom-setup form (events URL on the boolab.io domain when available,
  // .up.railway.app fallback otherwise).
  const startGchat = useMutation({
    mutationFn: async () => {
      if (!containerId || !orgId) {
        throw new Error("Your Boo isn't ready yet — please refresh.");
      }
      await api.onboarding.complete();
      return { orgId, containerId };
    },
    onSuccess: ({ orgId, containerId }) => {
      queryClient.invalidateQueries({ queryKey: ["session"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
      navigate({
        to: "/orgs/$orgId/containers/$containerId/connections/$connection",
        params: { orgId, containerId, connection: "google-chat" },
        replace: true,
      });
    },
  });

  // Skip button: mark onboarding complete and dashboard them. The
  // container is already running by this point, so no race against a
  // create POST — this is just a "finish onboarding without picking a
  // platform" path.
  const skipMutation = useMutation({
    mutationFn: () => api.onboarding.complete(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session"] });
      queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
    },
  });

  if (onboardingState.isError) {
    return <InlineError error={onboardingState.error ?? "Failed to load onboarding state"} />;
  }

  // ── Already connected → success state ──
  //
  // Two ways to land here:
  //   1. OAuth callback redirected back with ?slack=connected (we capture
  //      it into `slackResult` on mount; the useEffect strips the param
  //      but the state stays).
  //   2. User navigates back to the page after connecting earlier —
  //      onboardingState.slackConnected / gchatConnected reflect the row
  //      in the DB.
  //
  // Without this branch, the page just re-renders the picker after a
  // successful OAuth, the user thinks nothing happened, clicks "Add to
  // Slack" again, and the backend returns 409 already_installed — which
  // surfaces as raw "already_installed" text via InlineError.
  // Slack just connected through the shared app: pick Boo's default channel
  // before the success screen. Boo's greeting DM is held until this step's
  // Continue. Chief of staff skips it: one person, so the DM is the place.
  if (
    slackConnected &&
    !isCos &&
    containerId &&
    onboardingState.data?.slackKickoffPending &&
    defaultChoice === null
  ) {
    return (
      <DefaultChannelStep
        containerId={containerId}
        botName={botName}
        onDone={(choice) => {
          setDefaultChoice(choice);
          void queryClient.invalidateQueries({ queryKey: ["onboarding-state"] });
        }}
      />
    );
  }

  if (slackConnected || gchatConnected || teamsConnected || discordConnected) {
    const platformLabel = slackConnected
      ? "Slack"
      : teamsConnected
        ? "Microsoft Teams"
        : discordConnected
          ? "Discord"
          : "Google Chat";
    // Team id comes from onboarding state for either Slack path (shared app
    // or custom app); the shared-app status is the fallback.
    const slackTeamId = onboardingState.data?.slackTeamId ?? slackStatus.data?.teamId ?? null;
    const workspaceUrl =
      slackConnected && slackTeamId ? `https://app.slack.com/client/${slackTeamId}` : null;
    return (
      <div className="flex flex-col gap-8 items-center justify-center text-center py-8">
        <div className="relative">
          <span
            className="boo-pulse absolute -inset-8 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(127, 119, 221, 0.45), transparent 70%)",
            }}
          />
          <span
            className="boo-pulse absolute -inset-12 rounded-full"
            style={{
              background:
                "radial-gradient(closest-side, rgba(212, 83, 126, 0.25), transparent 70%)",
              animationDelay: "400ms",
            }}
          />
          <BooLogo className="relative size-20 rounded-2xl" />
        </div>
        <div className="space-y-2 max-w-sm">
          <h1 className="text-2xl font-semibold">
            {isCos
              ? `${botName} is in your ${platformLabel}`
              : slackConnected
                ? `${botName} is in your Slack — let's chat there!`
                : `${botName} is connected to ${platformLabel}!`}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isCos
              ? `Check your ${teamsConnected ? "chat" : "DMs"} to finish setting up.`
              : slackConnected
                ? defaultChoice?.kind === "channel"
                  ? (
                      <>
                        Look for a hello in your DMs. It'll check in at{" "}
                        <span className="whitespace-nowrap">{channelLabel(defaultChoice)}</span>.
                      </>
                    )
                  : `Open your workspace to DM or @mention ${botName}.`
                : `You're all set. Open ${platformLabel} to DM or @mention ${botName}, or jump into your dashboard.`}
          </p>
        </div>
        {workspaceUrl ? (
          <div className="flex w-full max-w-xs flex-col items-center gap-3">
            {/* New tab keeps this page alive so completing onboarding +
                the dashboard redirect aren't cut off by the navigation. */}
            <Button asChild className="w-full">
              <a
                href={workspaceUrl}
                target="_blank"
                rel="noreferrer"
                onClick={() => completeOnboarding.completeAndGo(orgId, containerId)}
              >
                Open Slack
              </a>
            </Button>
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
              disabled={completeOnboarding.isPending}
              onClick={() => completeOnboarding.completeAndGo(orgId, containerId)}
            >
              Go to dashboard
            </button>
          </div>
        ) : (
          <Button
            className="w-full max-w-xs"
            disabled={completeOnboarding.isPending}
            loading={completeOnboarding.isPending}
            onClick={() => completeOnboarding.completeAndGo(orgId, containerId)}
          >
            Go to dashboard
          </Button>
        )}
      </div>
    );
  }

  // ── Default: platform picker ──
  const hasError =
    slackResult === "error" ||
    gchatResult === "error" ||
    teamsResult === "error" ||
    discordResult === "error";
  const errorPlatform =
    slackResult === "error"
      ? "Slack"
      : teamsResult === "error"
        ? "Microsoft Teams"
        : discordResult === "error"
          ? "Discord"
          : "Google Chat";
  const workspaceTaken = slackResult === "workspace_taken";

  return (
    <div className="flex flex-col gap-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">
          {isCos ? `Where should ${botName} reach you?` : `Connect ${botName} to your team chat`}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isCos
            ? "Only you can message it there, until you add someone."
            : `Pick where your team chats. ${botName} will show up as a teammate you can DM or @mention.`}
        </p>
        {connectBusy && (
          <p className="text-xs text-muted-foreground">
            Finishing an integration connection, one moment…
          </p>
        )}
      </div>

      {workspaceTaken && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm">
          <p className="font-medium text-amber-900">
            {takenWorkspace ? `“${takenWorkspace}” is` : "That Slack workspace is"}{" "}
            already connected to another Boo workspace.
          </p>
          <p className="mt-1 text-amber-800">
            Connect a different Slack workspace below, or ask an admin of that
            workspace to invite you.
          </p>
        </div>
      )}

      {hasError && (
        <p className="text-sm text-destructive">
          Something went wrong connecting to {errorPlatform}. Please try again.
        </p>
      )}
      {addToSlack.isError &&
        !(addToSlack.error instanceof ApiError && addToSlack.error.code === "already_installed") && (
          <InlineError error={addToSlack.error} />
        )}
      {startGchat.isError && <InlineError error={startGchat.error} />}
      {addToTeams.isError &&
        !(addToTeams.error instanceof ApiError && addToTeams.error.code === "already_installed") && (
          <InlineError error={addToTeams.error} />
        )}
      {addToDiscord.isError &&
        !(addToDiscord.error instanceof ApiError && addToDiscord.error.code === "already_installed") && (
          <InlineError error={addToDiscord.error} />
        )}

      <div className="flex flex-col gap-3">
        {isCos ? (
          // Chief of staff: a custom Slack app under the exec's own name is
          // the promoted path; the shared Boo app stays one quiet link away.
          <CosSlackConnect
            containerId={containerId}
            defaultName={cosSlackName}
            disabled={startGchat.isPending || addToTeams.isPending || connectBusy}
            onQuickConnect={() => addToSlack.mutate()}
            quickPending={addToSlack.isPending}
          />
        ) : (
        <button
          type="button"
          disabled={addToSlack.isPending || startGchat.isPending || addToTeams.isPending || addToDiscord.isPending || !containerId || connectBusy}
          onClick={() => addToSlack.mutate()}
          className="flex items-center gap-4 rounded-xl border border-border bg-muted/30 px-5 py-4 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
        >
          <SlackIcon className="size-8 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium">Add {botName} to Slack</div>
            {!isCos && (
              <div className="text-xs text-muted-foreground">
                Quick install in any Slack workspace.
              </div>
            )}
          </div>
          {addToSlack.isPending && (
            <span className="text-xs text-muted-foreground">Connecting…</span>
          )}
        </button>
        )}

        {!isCos && (
        <button
          type="button"
          disabled={addToSlack.isPending || startGchat.isPending || addToTeams.isPending || addToDiscord.isPending || !containerId || connectBusy}
          onClick={() => startGchat.mutate()}
          className="flex items-center gap-4 rounded-xl border border-border bg-muted/30 px-5 py-4 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
        >
          <GchatIcon className="size-8 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium">Add {botName} to Google Chat</div>
            <div className="text-xs text-muted-foreground">
              For Google Workspace teams. Uses your own Google Cloud project.
            </div>
          </div>
          {startGchat.isPending && (
            <span className="text-xs text-muted-foreground">Setting up…</span>
          )}
        </button>
        )}

        {!isCos && teamsFlagEnabled && (
          <button
            type="button"
            disabled={
              addToSlack.isPending ||
              startGchat.isPending ||
              addToTeams.isPending ||
              addToDiscord.isPending ||
              !containerId ||
              connectBusy
            }
            onClick={() => addToTeams.mutate()}
            className="flex items-center gap-4 rounded-xl border border-border bg-muted/30 px-5 py-4 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            <TeamsIcon className="size-8 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">Add {botName} to Microsoft Teams</div>
              {!isCos && (
                <div className="text-xs text-muted-foreground">
                  Quick install in your Teams organization.
                </div>
              )}
            </div>
            {addToTeams.isPending && (
              <span className="text-xs text-muted-foreground">Connecting…</span>
            )}
          </button>
        )}

        {!isCos && discordFlagEnabled && brainSupportsDiscord && (
          <button
            type="button"
            disabled={
              addToSlack.isPending ||
              startGchat.isPending ||
              addToTeams.isPending ||
              addToDiscord.isPending ||
              !containerId ||
              connectBusy
            }
            onClick={() => addToDiscord.mutate()}
            className="flex items-center gap-4 rounded-xl border border-border bg-muted/30 px-5 py-4 text-left transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            <DiscordIcon className="size-8 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-medium">Add {botName} to Discord</div>
              {!isCos && (
                <div className="text-xs text-muted-foreground">
                  Quick install in your Discord server.
                </div>
              )}
            </div>
            {addToDiscord.isPending && (
              <span className="text-xs text-muted-foreground">Connecting…</span>
            )}
          </button>
        )}
      </div>

      <button
        type="button"
        className="text-sm text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
        disabled={skipMutation.isPending}
        onClick={() =>
          skipMutation.mutate(undefined, {
            onSuccess: () => navigate({ to: "/", replace: true }),
          })
        }
      >
        {skipMutation.isPending
          ? "Finishing up…"
          : isCos
            ? "Skip for now"
            : "Skip — I'll set this up later"}
      </button>
    </div>
  );
}
